/**
 * ★ THE SEAM. The ONE place the cabinet chooses an identity implementation. Every screen, hook and
 * reducer in this feature takes an `AuthClient`; only this file knows which one it is.
 *
 * THE SWAP IS MADE. `VITE_AUTHORS_AUTH=firebase` builds the real adapter,
 * ./firebase-auth-client.ts; any other value, and a build with no env file at all, keeps the
 * fixture exactly as before. What the adapter needed is kept below, with where each item stands.
 *
 * ── WHAT THE REAL ADAPTER NEEDS ─────────────────────────────────────────────────────────────────
 *
 * 1. THE DEPENDENCY - DONE. `firebase` 12.19.0, the web SDK, is a dependency of this app
 *    (web/authors/package.json). ./firebase-auth-client.ts imports `firebase/app` and
 *    `firebase/auth` statically - the default build bundles it (its header says why) but never
 *    initialises it: `initializeApp` runs in the firebase branch of `authClient()` below and nowhere
 *    else. `firebase/storage` is only in the uploader's lazily loaded chunk (features/storage/).
 *
 * 2. POPUP FIRST, AND ONLY POPUP FOR NOW. "Browser sign-in is the cold-start path only: the
 *    ordinary Firebase JS SDK, popup first" (adr/0007-web-app-stack.md). Do NOT wire
 *    `signInWithRedirect` / `getRedirectResult` until every item in ./redirect-fallback.ts is live:
 *    before then, redirect fails wherever third-party storage is blocked, which is every current
 *    Chrome, Safari and Firefox.
 *
 * 3. `authDomain` STAYS AT THE FIREBASE DEFAULT until prerequisite one and two land together.
 *    Pointing it at authors.moonx.app without the `/__/auth/*` proxy breaks popup sign-in too.
 *
 * 4. THE CSP MUST ALREADY ALLOW IT. The cabinet needs the Firebase Auth hosts in
 *    script-src / connect-src / frame-src — "popup sign-in loads Google's script and opens a
 *    frame". Starting set: apis.google.com (script-src), hoolimoon.firebaseapp.com (frame-src),
 *    firebasestorage.googleapis.com (img-src + connect-src). `securetoken.google.com` is NOT a CSP
 *    host — it is the `iss` claim, "named so M0 does not add it by mistake"
 *    (adr/0007-web-app-stack.md and :46; docs/design/12-build-plan.md). THE HEADERS ARE
 *    NOT OURS TO EMIT: the Caddy fragment sets them and no process of ours does.
 *
 * 5. THE ERROR MAPPING BELOW. `MAPPED_SDK_ERRORS` is the ONE table that turns the SDK's own error
 *    codes into this feature's states. The strings still appear nowhere in the corpus; they are
 *    now VERIFIED against the installed SDK instead, each against the line that raises it, and the
 *    table says where. Nothing else in this feature has to know them, which is the point of
 *    keeping them here.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 */
import { mockAuthClient, applyMockAuthScenarioFromSearch } from './mock-auth-client';
import { createFirebaseAuthClient } from './firebase-auth-client';
import {
  PopupBlockedError,
  PopupCancelledError,
  ProviderAlreadyLinkedError,
  ProviderUnreachableError,
} from './auth-port';
import type { AuthClient } from './auth-port';

export type AuthMode = 'mock' | 'firebase';

/** The feature states an SDK failure can mean. Every other code is "unrecognised". */
export type SdkFailureState = 'blocked' | 'cancelled' | 'unreachable' | 'already-linked';

/**
 * VERIFIED ON 2026-09-23 AGAINST THE INSTALLED SDK, each string against the line that RAISES it on
 * a path this app uses, not against a list of names. `SDK:NNN` is a line of `@firebase/auth`
 * 1.13.6's browser build, dist/esm/index-4NFEPWkC.js (./firebase-auth-client.ts says why that
 * file). Every code arrives as `auth/` plus the SDK's own code, because its error factory is built
 * for the service `auth` (SDK:308, :2671) and prefixes it (@firebase/util dist/index.esm.js).
 *
 *   auth/popup-blocked              `window.open` returned nothing (SDK:10633-10634)
 *   auth/popup-closed-by-user       the window was seen closed, then 8 s of grace (SDK:9787-9798)
 *   auth/cancelled-popup-request    a second popup superseded this one (SDK:9736-9738, :9773-9775)
 *   auth/user-cancelled             the person declined at Apple or Google; the SDK re-raises the
 *                                   provider handler's own code (SDK:10226-10230)
 *   auth/network-request-failed     a request failed or timed out (SDK:975-982, :1036-1039), or
 *                                   Google's script or the auth iframe never answered
 *                                   (SDK:10416-10424, :10450-10452, :10543-10548)
 *   auth/internal-error             chiefly: Google's sign-in script, which the popup path loads
 *                                   before it opens any window (SDK:9529, :10804, :10529, :10456,
 *                                   :11314), did not load at all - offline, or refused by a CSP or
 *                                   a content blocker (SDK:11304-11307). The SDK's own assertions
 *                                   raise it too, rarely; "try again" answers both
 *   auth/credential-already-in-use  a LINK whose Apple or Google account already belongs to
 *                                   another account (SDK:954-955); a cold start never raises it
 *
 * AGAINST THE UNVERIFIED TABLE: all six of its strings are real and all six are raised on the popup
 * path, so none was wrong. One row is new - `ProviderAlreadyLinkedError` was a port state nothing
 * produced (./auth-port.ts), and `credential-already-in-use` is its case exactly.
 *
 * DELIBERATELY UNMAPPED, so each surfaces as "unrecognised", naming its code:
 *   auth/web-storage-unsupported    the popup's iframe has no storage (SDK:9762-9765)
 *   auth/unauthorized-domain        this origin is not an authorized domain (SDK:10311-10328) -
 *                                   production only; the emulator skips the check (:10313-10315)
 * Neither is a blocked window, a change of mind or an unreachable provider, and a new failure state
 * is a screen's decision rather than this table's. Two strings are absent for another reason:
 * `auth/provider-already-linked` comes only from `_assertLinkedStatus` (SDK:5876-5882), which the
 * popup link never calls (SDK:9718-9724), and `auth/timeout` is raised by nothing in this build - a
 * timeout arrives as `network-request-failed` (SDK:1038).
 *
 * Still data rather than a switch statement, so that re-checking it is reading one constant.
 */
export const MAPPED_SDK_ERRORS: Readonly<Record<string, SdkFailureState>> = {
  'auth/popup-blocked': 'blocked',
  'auth/popup-closed-by-user': 'cancelled',
  'auth/cancelled-popup-request': 'cancelled',
  'auth/user-cancelled': 'cancelled',
  'auth/network-request-failed': 'unreachable',
  'auth/internal-error': 'unreachable',
  'auth/credential-already-in-use': 'already-linked',
};

/**
 * The mapping applied. Exported so the real adapter uses it verbatim rather than re-implementing
 * the same table a second time in a catch block - `authClient()` below hands it over.
 *
 * An unrecognised code becomes a plain `Error` that NAMES the code, with the SDK's error as its
 * cause: the screen says "something went wrong", and whoever reads the console sees which thing.
 */
export function toAuthPortError(sdkErrorCode: string, cause?: unknown): Error {
  switch (MAPPED_SDK_ERRORS[sdkErrorCode]) {
    case 'blocked':
      return new PopupBlockedError(cause);
    case 'cancelled':
      return new PopupCancelledError(cause);
    case 'unreachable':
      return new ProviderUnreachableError(cause);
    case 'already-linked':
      return new ProviderAlreadyLinkedError(cause);
    default:
      return new Error(`Unrecognized sign-in failure (${sdkErrorCode}).`, { cause });
  }
}

function readAuthMode(): AuthMode {
  const configured: unknown = import.meta.env.VITE_AUTHORS_AUTH;
  return configured === 'firebase' ? 'firebase' : 'mock';
}

export const AUTH_MODE: AuthMode = readAuthMode();

let client: AuthClient | null = null;

/**
 * The accessor every consumer uses. Memoised, because an auth client with two instances has two
 * observer sets and the second one never fires.
 */
export function authClient(): AuthClient {
  if (client !== null) {
    return client;
  }

  if (AUTH_MODE === 'firebase') {
    // THE SWAP, made. The SDK is initialised here - once, because `client` is memoised above - and
    // nowhere else. A build with no Firebase config throws here, naming the missing variable.
    client = createFirebaseAuthClient(toAuthPortError);
    return client;
  }

  if (typeof window !== 'undefined') {
    applyMockAuthScenarioFromSearch(window.location.search);
  }
  client = mockAuthClient;
  return client;
}

/** Tests replace the client wholesale; production never calls this. */
export function setAuthClientForTest(next: AuthClient | null): void {
  client = next;
}
